import React, { useState, useMemo } from "react";
import { Box, Popover, IconButton, Tooltip, Typography, Checkbox, TextField, InputAdornment } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Calendar, Building2, Plug, RotateCcw, Search } from "lucide-react";
import { DateRange } from "react-date-range";
import { ptBR } from "date-fns/locale";
import { format, formatDistanceToNow, subDays } from "date-fns";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

// new Date("2026-04-01") parseia como UTC midnight ⇒ em BRT (UTC-3) vira
// 2026-03-31T21:00 local, e o react-date-range pinta o dia anterior.
// Helper constrói Date no fuso local a partir da string yyyy-MM-dd do filtro.
const parseLocalDate = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    toolbar: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: 8,
      padding: "12px 16px",
      borderRadius: 14,
      background: dark ? "rgba(18,18,19,0.95)" : "#ffffff",
      border: dark ? "1px solid rgba(91,124,255,0.12)" : "1px solid #e2e8f0",
    },
    chip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "7px 12px",
      borderRadius: 999,
      cursor: "pointer",
      fontSize: 12,
      fontWeight: 500,
      letterSpacing: "0.01em",
      border: dark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #e2e8f0",
      background: dark ? "rgba(255,255,255,0.03)" : "#f8fafc",
      color: dark ? "rgba(255,255,255,0.85)" : "#1e293b",
      transition: "all 140ms ease",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.07)" : "#f1f5f9",
        borderColor: dark ? "rgba(91,124,255,0.32)" : "#cbd5e1",
        transform: "translateY(-1px)",
      },
    },
    chipActive: {
      background: dark ? "rgba(91,124,255,0.14)" : "rgba(91,124,255,0.08)",
      borderColor: dark ? "rgba(91,124,255,0.42)" : "rgba(91,124,255,0.32)",
      color: dark ? "#a5b4fc" : "#4338ca",
    },
    chipIcon: {
      opacity: 0.85,
      flexShrink: 0,
    },
    spacer: { flex: 1 },
    lastUpdate: {
      fontSize: 11,
      color: dark ? "rgba(255,255,255,0.42)" : "#94a3b8",
      letterSpacing: "0.02em",
    },
    refreshBtn: {
      marginLeft: 8,
      color: dark ? "rgba(255,255,255,0.7)" : "#475569",
      "&:hover": { color: dark ? "#fff" : "#0f172a" },
    },
    refreshing: { animation: "$spin 800ms linear infinite" },
    "@keyframes spin": {
      "0%": { transform: "rotate(0deg)" },
      "100%": { transform: "rotate(360deg)" },
    },
    popPaper: {
      padding: 0,
      borderRadius: 14,
      background: dark ? "#1c1f22" : "#ffffff",
      border: dark
        ? "1px solid rgba(255,255,255,0.06)"
        : "1px solid rgba(15,23,42,0.06)",
      boxShadow: dark
        ? "0 20px 40px -12px rgba(0,0,0,0.5), 0 6px 14px -4px rgba(0,0,0,0.3)"
        : "0 20px 40px -12px rgba(15,23,42,0.22), 0 6px 14px -4px rgba(15,23,42,0.10)",
    },
    multiList: {
      width: "min(320px, calc(100vw - 32px))",
      maxHeight: 380,
      overflow: "auto",
      padding: "8px 4px",
    },
    multiSearch: {
      padding: "8px 12px",
      borderBottom: dark ? "1px solid rgba(255,255,255,0.05)" : "1px solid #f1f5f9",
    },
    dateRangeWrap: {
      maxWidth: "calc(100vw - 32px)",
      overflowX: "auto",
    },
    multiItem: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      padding: "6px 10px",
      borderRadius: 8,
      cursor: "pointer",
      fontSize: 13,
      color: dark ? "rgba(255,255,255,0.85)" : "#1e293b",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.04)" : "#f8fafc",
      },
    },
    providerSegment: {
      display: "inline-flex",
      gap: 0,
      padding: 2,
      borderRadius: 999,
      background: dark ? "rgba(255,255,255,0.04)" : "#f1f5f9",
      border: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid #e2e8f0",
    },
    providerOption: {
      padding: "5px 12px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      cursor: "pointer",
      transition: "all 140ms ease",
      color: dark ? "rgba(255,255,255,0.55)" : "#64748b",
      border: "1px solid transparent",
    },
    providerOptionActive: {
      color: "#fff",
      background: "linear-gradient(135deg, #818cf8 0%, #6366f1 100%)",
    },
    rangePresets: {
      display: "flex",
      gap: 6,
      padding: "10px 12px",
      borderTop: dark ? "1px solid rgba(255,255,255,0.05)" : "1px solid #f1f5f9",
      flexWrap: "wrap",
    },
    presetBtn: {
      fontSize: 11,
      padding: "4px 10px",
      borderRadius: 999,
      cursor: "pointer",
      border: dark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #e2e8f0",
      background: "transparent",
      color: dark ? "rgba(255,255,255,0.7)" : "#475569",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.04)" : "#f8fafc",
        borderColor: dark ? "rgba(91,124,255,0.32)" : "#cbd5e1",
      },
    },
  };
});

const PROVIDERS = [
  { value: undefined, label: "Todos" },
  { value: "waha", label: "Não oficial" },
  { value: "meta-api", label: "API Oficial" },
];

const providerLabel = (p) => {
  if (p === "waha") return "Não oficial";
  if (p === "meta-api") return "API Oficial";
  return p || "";
};

const FiltersToolbar = ({
  filters,
  onChange,
  companies = [],
  whatsapps = [],
  lastUpdatedAt,
  isFetching,
  onRefresh,
  hideCompanyFilter = false,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const [pop, setPop] = useState(null);
  const popKey = pop?.key;
  const anchorEl = pop?.anchor;
  const [companySearch, setCompanySearch] = useState("");
  const [whatsappSearch, setWhatsappSearch] = useState("");

  const openPop = (key) => (e) => setPop({ key, anchor: e.currentTarget });
  const closePop = () => setPop(null);

  const filteredCompanies = useMemo(() => {
    const q = companySearch.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter(c => c.name?.toLowerCase().includes(q));
  }, [companies, companySearch]);

  const filteredWhatsapps = useMemo(() => {
    const q = whatsappSearch.trim().toLowerCase();
    let list = whatsapps;
    if (filters.companyIds?.length) {
      list = list.filter(w => filters.companyIds.includes(w.companyId));
    }
    if (q) list = list.filter(w => w.name?.toLowerCase().includes(q));
    return list;
  }, [whatsapps, whatsappSearch, filters.companyIds]);

  const dateRangeLabel = useMemo(() => {
    if (!filters.startDate || !filters.endDate) return "Período";
    const s = format(parseLocalDate(filters.startDate), "dd/MM");
    const e = format(parseLocalDate(filters.endDate), "dd/MM");
    return `${s} → ${e}`;
  }, [filters.startDate, filters.endDate]);

  const companyLabel = filters.companyIds?.length
    ? `${filters.companyIds.length} empresa${filters.companyIds.length > 1 ? "s" : ""}`
    : "Empresas";
  const whatsappLabel = filters.whatsappIds?.length
    ? `${filters.whatsappIds.length} conexão${filters.whatsappIds.length > 1 ? "ões" : ""}`
    : "Conexões";

  const lastUpdateText = useMemo(() => {
    if (!lastUpdatedAt) return null;
    try {
      return formatDistanceToNow(new Date(lastUpdatedAt), { addSuffix: true, locale: ptBR });
    } catch { return null; }
  }, [lastUpdatedAt]);

  const toggleId = (field, id) => {
    const current = filters[field] || [];
    const next = current.includes(id) ? current.filter(x => x !== id) : [...current, id];
    onChange({ ...filters, [field]: next });
  };

  const setPreset = (days) => {
    const end = new Date();
    const start = subDays(end, days - 1);
    onChange({
      ...filters,
      startDate: format(start, "yyyy-MM-dd"),
      endDate: format(end, "yyyy-MM-dd"),
    });
    closePop();
  };

  const dark = theme.palette.type === "dark";
  const dateRangeColor = dark ? "#818cf8" : "#6366f1";

  const rangeStart = filters.startDate ? parseLocalDate(filters.startDate) : subDays(new Date(), 29);
  const rangeEnd = filters.endDate ? parseLocalDate(filters.endDate) : new Date();

  return (
    <Box className={classes.toolbar}>
      {/* Período */}
      <Tooltip title="Período" arrow>
        <Box
          className={[classes.chip, filters.startDate ? classes.chipActive : ""].filter(Boolean).join(" ")}
          onClick={openPop("date")}
        >
          <Calendar size={14} className={classes.chipIcon} />
          {dateRangeLabel}
        </Box>
      </Tooltip>

      {/* Empresas — só aparece quando o componente é cross-tenant (super-admin) */}
      {!hideCompanyFilter && (
        <Tooltip title="Filtrar por empresa" arrow>
          <Box
            className={[classes.chip, filters.companyIds?.length ? classes.chipActive : ""].filter(Boolean).join(" ")}
            onClick={openPop("company")}
          >
            <Building2 size={14} className={classes.chipIcon} />
            {companyLabel}
          </Box>
        </Tooltip>
      )}

      {/* Conexões */}
      <Tooltip title="Filtrar por conexão" arrow>
        <Box
          className={[classes.chip, filters.whatsappIds?.length ? classes.chipActive : ""].filter(Boolean).join(" ")}
          onClick={openPop("whatsapp")}
        >
          <Plug size={14} className={classes.chipIcon} />
          {whatsappLabel}
        </Box>
      </Tooltip>

      {/* Provider */}
      <Box className={classes.providerSegment}>
        {PROVIDERS.map(p => {
          const active = (filters.provider ?? null) === (p.value ?? null);
          return (
            <Box
              key={p.label}
              className={[classes.providerOption, active ? classes.providerOptionActive : ""].filter(Boolean).join(" ")}
              onClick={() => onChange({ ...filters, provider: p.value })}
            >
              {p.label}
            </Box>
          );
        })}
      </Box>

      <Box className={classes.spacer} />

      {lastUpdateText && (
        <Typography className={classes.lastUpdate}>
          atualizado {lastUpdateText}
        </Typography>
      )}
      <Tooltip title="Atualizar agora" arrow>
        <IconButton
          size="small"
          className={classes.refreshBtn}
          onClick={onRefresh}
          disabled={isFetching}
        >
          <RotateCcw
            size={16}
            strokeWidth={2}
            className={isFetching ? classes.refreshing : ""}
          />
        </IconButton>
      </Tooltip>

      {/* Popover: período */}
      <Popover
        open={popKey === "date"}
        anchorEl={anchorEl}
        onClose={closePop}
        classes={{ paper: classes.popPaper }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box className={classes.dateRangeWrap}>
          <DateRange
            ranges={[{ startDate: rangeStart, endDate: rangeEnd, key: "selection" }]}
            onChange={(item) => {
              const r = item.selection;
              onChange({
                ...filters,
                startDate: format(r.startDate, "yyyy-MM-dd"),
                endDate: format(r.endDate, "yyyy-MM-dd"),
              });
            }}
            locale={ptBR}
            rangeColors={[dateRangeColor]}
            showDateDisplay={false}
            maxDate={new Date()}
          />
          <Box className={classes.rangePresets}>
            <button className={classes.presetBtn} onClick={() => setPreset(7)}>7d</button>
            <button className={classes.presetBtn} onClick={() => setPreset(15)}>15d</button>
            <button className={classes.presetBtn} onClick={() => setPreset(30)}>30d</button>
            <button className={classes.presetBtn} onClick={() => setPreset(60)}>60d</button>
            <button className={classes.presetBtn} onClick={() => setPreset(90)}>90d</button>
          </Box>
        </Box>
      </Popover>

      {/* Popover: empresas — só renderiza quando o filtro está visível */}
      {!hideCompanyFilter && (
        <Popover
          open={popKey === "company"}
          anchorEl={anchorEl}
          onClose={closePop}
          classes={{ paper: classes.popPaper }}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <Box className={classes.multiSearch}>
            <TextField
              placeholder="Buscar empresa..."
              value={companySearch}
              onChange={(e) => setCompanySearch(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={14} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box className={classes.multiList}>
            {filteredCompanies.map(c => {
              const checked = filters.companyIds?.includes(c.id);
              return (
                <Box
                  key={c.id}
                  className={classes.multiItem}
                  onClick={() => toggleId("companyIds", c.id)}
                >
                  <Checkbox checked={!!checked} size="small" color="primary" />
                  <span>{c.name}</span>
                </Box>
              );
            })}
          </Box>
          {filters.companyIds?.length > 0 && (
            <Box className={classes.rangePresets}>
              <button className={classes.presetBtn} onClick={() => onChange({ ...filters, companyIds: [] })}>Limpar</button>
            </Box>
          )}
        </Popover>
      )}

      {/* Popover: conexões */}
      <Popover
        open={popKey === "whatsapp"}
        anchorEl={anchorEl}
        onClose={closePop}
        classes={{ paper: classes.popPaper }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box className={classes.multiSearch}>
          <TextField
            placeholder="Buscar conexão..."
            value={whatsappSearch}
            onChange={(e) => setWhatsappSearch(e.target.value)}
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={14} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box className={classes.multiList}>
          {filteredWhatsapps.length === 0 ? (
            <Typography style={{ padding: 12, fontSize: 12, opacity: 0.6 }}>
              Nenhuma conexão {filters.companyIds?.length ? "para a(s) empresa(s) selecionada(s)" : ""}
            </Typography>
          ) : filteredWhatsapps.map(w => {
            const checked = filters.whatsappIds?.includes(w.id);
            return (
              <Box
                key={w.id}
                className={classes.multiItem}
                onClick={() => toggleId("whatsappIds", w.id)}
              >
                <Checkbox checked={!!checked} size="small" color="primary" />
                <span style={{ flex: 1 }}>{w.name}</span>
                <span style={{ fontSize: 10, opacity: 0.5, textTransform: "uppercase" }}>{providerLabel(w.provider)}</span>
              </Box>
            );
          })}
        </Box>
        {filters.whatsappIds?.length > 0 && (
          <Box className={classes.rangePresets}>
            <button className={classes.presetBtn} onClick={() => onChange({ ...filters, whatsappIds: [] })}>Limpar</button>
          </Box>
        )}
      </Popover>
    </Box>
  );
};

export default FiltersToolbar;
